import React, { useRef, useEffect, useCallback } from "react"
import _clamp from "lodash/clamp"
import { scaleLinear } from "d3-scale"
import { easeQuadInOut } from "d3-ease"
import { useSpring, animated as a } from "react-spring"
import { Typography, TypographyVariant, SvgIconProps } from "@mui/material"

import { IconWrap } from "./Gauge.styles"
import { getLineCoordinates } from "../../utils/trig"

type TSize = "small" | "medium" | "large" | "xlarge"

const needleWidth: { [key in TSize]: number } = {
  small: 1.5,
  medium: 1.75,
  large: 2,
  xlarge: 3
}

interface INeedleProps {
  size: TSize
  value: number
  color?: string
  range: number[]
  domain?: number[]
  innerRadius: number
  outerRadius: number
}

export const Needle = ({
  size,
  value,
  innerRadius,
  outerRadius,
  color = "#fff",
  domain = [0, 10],
  range = [0, 360]
}: INeedleProps) => {
  const getScaledNeedle = scaleLinear(domain, range)
  const theta = getScaledNeedle(value)

  const { x1, y1, x2, y2 } = getLineCoordinates(theta, innerRadius, outerRadius)

  const needleProps = {
    stroke: color,
    strokeWidth: needleWidth[size]
  }
  // using two lines to only have the line in the outer circ with a round cap
  // this line starts further down (multiplied by 1.2) to not have the round cap on the inner side
  return (
    <>
      <line
        x1={x1 * 1.2}
        y1={y1 * 1.2}
        x2={x2}
        y2={y2}
        {...needleProps}
        strokeLinecap="round"
      />
      <line x1={x1} y1={y1} x2={x2} y2={y2} {...needleProps} />
    </>
  )
}

interface ILabelProps {
  size?: TSize
  min?: number
  max?: number
  svg?: boolean
  scale?: number
  children: React.ReactNode
  variant?: TypographyVariant
  addProps?: { [key: string]: any }
  addStyles?: { [key: string]: any }
}

const sizeOptions: { [key in TSize]: number } = {
  small: 140,
  medium: 180,
  large: 200,
  xlarge: 300
}

export const ScaledTypography = ({
  children,
  min = 0.75,
  max = 2,
  scale = 1,
  size = "medium",
  addProps = {},
  addStyles = {},
  variant = "h5"
}: ILabelProps): React.ReactElement => {
  const sizeValue = (sizeOptions[size] * scale) / 128

  // clamp the size of text between a min and max value to avoid size that is way too small or too big
  const clampedSize = `${_clamp(sizeValue, min, max)}rem`

  return (
    <Typography
      variant={variant}
      style={{ ...addStyles, fontSize: clampedSize }}
      {...addProps}
    >
      {children}
    </Typography>
  )
}

type TOnChangeFn = (value: number) => any

interface ICountTypography {
  value: number
  size?: TSize
  prefix?: string
  suffix?: string
  decimalPlaces: number
  color?: string
  onChangeFn?: TOnChangeFn
}

export const CountTypography = React.memo(
  ({
    size,
    value,
    color,
    prefix = "",
    suffix = "",
    onChangeFn,
    decimalPlaces
  }: ICountTypography) => {
    const prevValueRef = useRef<number>(0)

    const [countAnimation, countAnimationApi] = useSpring(() => ({
      from: { opacity: 0.5, animatedValue: 0 },
      to: { opacity: 1, animatedValue: value },
      animatedValue: value,
      reset: true,
      config: {
        easing: easeQuadInOut,
        duration: 1000
      }
    }))

    const triggerAnimation = useCallback(() => {
      countAnimationApi.start({
        from: { opacity: 0.5, animatedValue: prevValueRef.current },
        to: { opacity: 1, animatedValue: value }
      })

      prevValueRef.current = value
    }, [countAnimationApi, value])

    useEffect(() => {
      triggerAnimation()
    }, [value, countAnimationApi, triggerAnimation])

    const AnimatedTypography = a(ScaledTypography)

    return (
      <AnimatedTypography min={0.8} size={size} addStyles={{ color }}>
        {countAnimation.animatedValue.to((val: any) => {
          onChangeFn && onChangeFn(+val)
          return `${prefix}${val.toFixed(decimalPlaces)}${suffix}`
        })}
      </AnimatedTypography>
    )
  },
  (prevProps, nextProps) => prevProps.value === nextProps.value
)

interface IIcon {
  width: number
  Icon: React.FunctionComponent<SvgIconProps>
  color?: string
}

export const WrappedIcon = ({ width, Icon, color = "#FFF" }: IIcon) => {
  const iconSize = width / 16
  const wrapperSize = width / 12

  return (
    <IconWrap size={wrapperSize} color={color}>
      <Icon style={{ width: iconSize, height: iconSize, color }} />
    </IconWrap>
  )
}
